import { AuthenticatedImage } from '@components/AuthenticatedImage/AuthenticatedImage';
import {
  billedToOwnerFailureNotice,
  BilledToOwnerNotice,
} from '@components/ChooseMediaModal/BilledToOwnerNotice';
import { FailedMediaListModal } from '@components/FailedMediaList/FailedMediaListModal';
import { MediaListLink } from '@components/FailedMediaList/MediaListLink';
import { AudioSelectedIcon } from '@components/Icons/AudioSelectedIcon';
import { VideoSelectedIcon } from '@components/Icons/VideoSelectedIcon';
import { InlineConfirm } from '@components/InlineConfirm/InlineConfirm';
import { PlaceholderGradient } from '@components/util/PlaceholderGradient';
import { useTopUp } from '@lib/hooks/useTopUp';
import { generateIdFromString } from '@lib/utils/colorUtils';
import {
  CancelOutlined,
  CheckCircleRounded,
  CloseRounded,
  MoreVertRounded,
  WarningAmberRounded,
} from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { notificationStore, SkippedDuplicateRow } from '@stores/Notifications';
import { trackingStore } from '@stores/Tracking';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import css from './BatchCard.module.scss';
import { BatchCardMenu } from './BatchCardMenu';
import { BatchState } from './batchState';

const MAX_TOOLTIP_TITLES = 5;

/**
 * Pre-computed render data for the BatchCard. The wrapper `BatchCardLive`
 * is responsible for reading `notificationStore` / `uploadManager` /
 * `useMediaThumbnail` and reducing them into this VM. The dumb component
 * below takes the VM as-is and renders without any further store access —
 * which is what lets `ToastDebugPage` mount BatchCard with mock VMs to
 * simulate states a real upload would otherwise be required to reach.
 */
export interface BatchCardViewModel {
  id: string;
  /** Legacy title (typically library name or custom retry-batch title).
   * Kept as a defensive fallback for the header when `mediaTitles` is empty. */
  title: string;
  /** Library name for the new subtitle line. */
  libraryName: string;
  /** Path the kebab's "Show in library" item navigates to, and the
   * library-name subtitle's link target. */
  libraryUrl: string;
  state: BatchState;
  mediaCount: number;
  /** Per-media filenames. mediaTitles[0] is shown in the header;
   * mediaTitles.slice(1) is shown in the hover tooltip on "+ X others". */
  mediaTitles: string[];
  isAudio: boolean;
  thumbnailUrl: string | null;
  secondaryThumbnailUrl: string | null;
  gradientSeed: string | null;
  secondaryGradientSeed: string | null;
  /** CDN preview overlays. Live wrapper resolves these via `useMediaThumbnail`;
   * the dumb component renders them on top of whatever local content is
   * underneath when set. */
  primaryCdnPreview: { url: string; token: string } | null;
  secondaryCdnPreview: { url: string; token: string } | null;
  /** 0–1, multiplied by 100 for the progress bar. */
  displayProgress: number;
  /** Slice of displayProgress (0–1) that's failed/cancelled, rendered as a
   *  distinct red segment of the bar (upload phase only). */
  failedProgress: number;
  /** Already-resolved time-line copy; wrapper computes this since it depends
   * on `uploadManager.getBatchUploadStats()` / `uploadingFiles.length` for
   * uploading + queued states. Null = no time-line for this state (wrap-up,
   * or nothing relevant for terminal — those fall through to formatTime). */
  timeLineText: string | null;
  /** Every file that failed in this batch — what the status copy reports. */
  failedCount: number;
  /** The failures the user can still act on: the Results modal lists them and
   * Retry re-runs them. Fewer than `failedCount` once an upload failure has been
   * retried or removed, or after a reload, which loses the File it would retry. */
  retryableCount: number;
  succeededCount: number;
  hasBillingFails: boolean;
  showCreditErrors: boolean;
  showStorageErrors: boolean;
  /** Every failure in the batch was the user's connection dropping (see BatchCardLive). */
  allConnectionFails: boolean;
  /**
   * True when the backend bills this batch's target library to another account (see
   * `resolveBillingOwnership` — the billing axis, not who owns the library, since org accounts
   * share one billing account). Only meaningful alongside `hasBillingFails`, where it decides
   * *whose* wallet ran out: the default row's top-up CTA charges the current user, which is the
   * wrong account here and would take money without unblocking a single file.
   */
  billedToAnotherAccount: boolean;
  /**
   * The library owner's name, when the backend gave one — the failure row names them, which is
   * what turns "you can't fix this" into "ask this person". Undefined is a real case (an owner
   * with neither a name nor an email, or a library billed elsewhere that the reader owns) and
   * must not be read as "not cross-billed"; the flag above answers that.
   */
  ownerName?: string;
  /** Used by terminal states' time-line via `formatTime(timestamp)`. */
  timestamp: number;
  /** Files filtered out as duplicates before this batch's non-duplicate
   * files were uploaded. Surfaced via a "View N duplicates skipped"
   * `MediaListLink` (info variant) at the bottom of the card. Empty hides
   * the affordance. When a row carries `libraryId` + `mediaId`, the modal
   * row also renders a "View in library" link. */
  skippedDuplicates: SkippedDuplicateRow[];
}

interface BatchCardProps {
  vm: BatchCardViewModel;
  onDismiss: () => void;
  formatTime: (timestamp: number) => string;
  /** Already bound to the notification id by the wrapper. */
  onOpenResults?: () => void;
  onRetryFailed?: () => void;
  onDeleteBatch?: () => void;
  onCancelBatch?: () => void;
}

function buildTitlesTooltip(titles: string[]): React.ReactNode {
  if (titles.length === 0) return null;
  const shown = titles.slice(0, MAX_TOOLTIP_TITLES);
  const remaining = titles.length - shown.length;
  return (
    <div>
      {shown.map((t, i) => (
        <div key={i}>{t}</div>
      ))}
      {remaining > 0 && <div>and {remaining} more...</div>}
    </div>
  );
}

/** The "View" pills' count. Once part of the batch's failures can no longer be
 * retried, it is smaller than the failed count the status line reports, so it
 * names its share of that count instead of reading as a second total. */
function failedFilesSubtext(retryableCount: number, failedCount: number): string {
  if (retryableCount < failedCount) return `${retryableCount} of ${failedCount} failed files`;
  return `${retryableCount} failed ${retryableCount === 1 ? 'file' : 'files'}`;
}

function SpinnerArc() {
  return (
    <svg viewBox="0 0 24 24">
      <path className={css.arc} d="M 12 2 A 10 10 0 0 1 22 12" />
    </svg>
  );
}

/**
 * Billing-failure row for a library billed to **another** account. Deliberately a separate
 * component rather than a branch inside `BillingActionRow`, so `useTopUp` is never mounted on
 * this path: every figure it produces describes the current user's wallet, and the failure here
 * is the *owner's* balance. Offering "Top-up 250,000 credits for $20" would charge the wrong
 * account and leave every file still failing — money spent for nothing.
 *
 * There is no self-service recovery to offer, so the row explains that and keeps only the two
 * actions that still mean something: see which files failed, and retry once the owner has
 * topped up.
 */
function OwnerBilledActionRow({
  retryableCount,
  failedCount,
  ownerName,
  onRetry,
  onOpenResults,
}: {
  retryableCount: number;
  failedCount: number;
  ownerName?: string;
  onRetry?: () => void;
  onOpenResults?: () => void;
}) {
  const fileWord = retryableCount === 1 ? 'file' : 'files';
  return (
    <>
      <BilledToOwnerNotice
        message={billedToOwnerFailureNotice(ownerName)}
        className={css.billedToOwnerNotice}
      />
      {onOpenResults && (
        <MediaListLink
          variant="error"
          action="View"
          subtext={failedFilesSubtext(retryableCount, failedCount)}
          onClick={() => onOpenResults()}
        />
      )}
      <MediaListLink
        variant="warning"
        action="Retry"
        subtext={`${retryableCount} ${fileWord}`}
        onClick={() => onRetry?.()}
        disabled={!onRetry}
      />
    </>
  );
}

/**
 * Billing-failure action row. Mounts `useTopUp` (subscribes to billingStore)
 * inline — it only renders when `vm.hasBillingFails`, so the hook is gated
 * by parent JSX. Self-contained in the dumb component because all of its
 * inputs are already resolved booleans on the VM.
 *
 * Only for libraries billed to the current user — `OwnerBilledActionRow` above handles the
 * cross-billed case, where a top-up of this user's account is the wrong remedy.
 */
function BillingActionRow({
  showStorageErrors,
  showCreditErrors,
  retryableCount,
  failedCount,
  onRetry,
  onOpenResults,
}: {
  showStorageErrors: boolean;
  showCreditErrors: boolean;
  retryableCount: number;
  failedCount: number;
  onRetry?: () => void;
  onOpenResults?: () => void;
}) {
  const topUp = useTopUp({
    creditCost: showCreditErrors ? 1 : 0,
    currentCredits: 0,
    insufficient: showCreditErrors,
    source: 'upload-status-batch-card',
    ...(showStorageErrors && {
      storage: { afterProcessing: -1, duration: 0 },
    }),
  });

  const fileWord = retryableCount === 1 ? 'file' : 'files';
  // Live label from useTopUp — "Top-up 250,000 credits for $20" / "Upgrade"
  // / "Contact sales". Falls back to a sensible default if the hook returns
  // null (same as the rich-event billing path).
  const topUpLabel = topUp.label ?? (showStorageErrors ? 'Upgrade plan' : 'Top up credits');

  // Three stacked CTAs in the same shape as the rest of the panel: top-up
  // (info / pink), view failed files (warning), retry (warning). Replaces
  // the prior two-button row so every "billing fail" surface speaks the
  // same visual language as the MediaListLink pills elsewhere.
  return (
    <>
      <MediaListLink
        variant="info"
        action={topUpLabel}
        onClick={() => topUp.handleTopUp()}
        disabled={topUp.isToppingUp}
      />
      {onOpenResults && (
        <MediaListLink
          variant="error"
          action="View"
          subtext={failedFilesSubtext(retryableCount, failedCount)}
          onClick={() => onOpenResults()}
        />
      )}
      <MediaListLink
        variant="warning"
        action="Retry"
        subtext={`${retryableCount} ${fileWord}`}
        onClick={() => onRetry?.()}
        disabled={!onRetry}
      />
    </>
  );
}

// React.memo bails the render when every prop is shallow-equal to last
// render. The wrapping is only useful in proportion to how stable the
// props actually are upstream, so depends on the call site:
//  - `vm` is `useMemo`-stabilised in `BatchCardLive`.
//  - `onOpenResults` / `onRetryFailed` / `onDeleteBatch` / `onCancelBatch`
//    are `useCallback`-stabilised in `BatchCardLive`.
//  - `onDismiss` and `formatTime` are pass-through — they need to be
//    stable at the *caller* of `BatchCardLive` for memo to bail.
//    `UploadStatus.tsx`'s `NotificationCard` wraps the bound dismiss
//    in `useCallback` and uses a module-scope `formatTime`, so the
//    production path is stable. `ToastDebugPage`'s sim path passes
//    fresh inline arrows, but the VM there is changing every tick
//    anyway — memo can't bail and isn't expected to.
//
// Net: memo is a real win on the production path during 500ms idle
// MobX ticks where the VM doesn't change; on the debug-sim path it's
// a harmless no-op.
function BatchCardImpl({
  vm,
  onDismiss,
  formatTime,
  onOpenResults,
  onRetryFailed,
  onDeleteBatch,
  onCancelBatch,
}: BatchCardProps) {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [pendingCancel, setPendingCancel] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  // State-derived booleans. Pure functions of vm.state — no store reads.
  const { state } = vm;
  const isQueued = state === 'queued';
  const isPreparing = state === 'preparing';
  const isUploading = state === 'uploading';
  // A stalled batch is still mid-transfer — it must count as active everywhere
  // 'uploading' does. Omitting it here would make the card read as terminal:
  // a timestamp instead of live copy, and "Remove from list" offered on an
  // upload that is still going.
  const isStalled = state === 'stalled';
  const isWrappingUp = state === 'wrapping-up';
  const isProcessing = state === 'processing';
  const isActive =
    isQueued || isPreparing || isUploading || isStalled || isWrappingUp || isProcessing;
  // Cancel applies through queued / preparing / uploading / stalled. Past
  // wrap-up, finishFileUpload is in flight for EVERY started file and racing it
  // would be inconsistent. Stalled is included because a dying transfer is
  // exactly when someone wants out — its exposure is identical to 'uploading':
  // in a multi-file batch either state can coexist with one sibling file
  // already in its finish POST, and cancelling races that finish. That
  // (pre-existing) race is accepted for 'uploading' and therefore equally
  // acceptable here; 'stalled' also can't mean "everything finished, finishes
  // draining" — isBatchStalled ignores files at 100%, so at least one file is
  // genuinely mid-transfer whenever this state shows.
  const canCancel = isQueued || isPreparing || isUploading || isStalled;

  // Auto-dismiss the overlay once cancel no longer applies — the last file
  // finished transferring while the user sat on the confirm, so the window
  // has closed. Leaving the overlay up would be misleading.
  useEffect(() => {
    if (pendingCancel && !canCancel) {
      setPendingCancel(false);
    }
  }, [pendingCancel, canCancel]);

  const percent = Math.round(vm.displayProgress * 100);
  // `mediaTitles[0]` drives the header; fall back to legacy `vm.title` (typically
  // the library name) when the titles array is empty — shouldn't happen in
  // practice but keeps the card from rendering blank if a notification was
  // persisted without titles.
  const filename = vm.mediaTitles[0] ?? vm.title;
  const othersCount = Math.max(0, vm.mediaCount - 1);
  const othersTooltip = buildTitlesTooltip(vm.mediaTitles.slice(1));

  const titleStrikethrough = state === 'cancelled-all' || state === 'done-total-failure';

  const { statusText, statusIcon, statusClass } = (() => {
    switch (state) {
      case 'done-success':
        return {
          statusText: 'Complete',
          statusIcon: <CheckCircleRounded />,
          statusClass: css.statusDone,
        };
      case 'done-partial-failure':
        return {
          statusText: 'Partial failure',
          statusIcon: <WarningAmberRounded />,
          statusClass: css.statusWarn,
        };
      case 'done-total-failure':
        return {
          statusText: 'Upload failed',
          statusIcon: <CancelOutlined />,
          statusClass: css.statusFailed,
        };
      case 'cancelled-all':
        return {
          statusText: 'Upload canceled',
          statusIcon: <CancelOutlined />,
          statusClass: css.statusCancelled,
        };
      default:
        return { statusText: null, statusIcon: null, statusClass: '' };
    }
  })();

  const subText: string | null = (() => {
    switch (state) {
      case 'queued':
        return 'Queued for upload…';
      case 'preparing':
        return 'Preparing for upload…';
      case 'uploading':
        return 'Uploading…';
      case 'stalled':
        return 'Waiting for a stable connection…';
      case 'wrapping-up':
        return 'Wrapping up upload…';
      case 'processing':
        return 'Processing…';
      case 'done-success':
        return `${vm.mediaCount} media ready in your library`;
      case 'done-partial-failure': {
        const succ = vm.succeededCount;
        if (vm.showStorageErrors)
          return `${succ} succeeded · ${vm.failedCount} failed (not enough storage)`;
        if (vm.showCreditErrors)
          return `${succ} succeeded · ${vm.failedCount} failed (not enough credits)`;
        if (vm.allConnectionFails)
          return `${succ} succeeded · ${vm.failedCount} failed (unstable connection)`;
        return `${succ} succeeded · ${vm.failedCount} failed`;
      }
      case 'done-total-failure': {
        if (vm.showStorageErrors) return 'Not enough storage';
        if (vm.showCreditErrors) return 'Not enough credits to process';
        if (vm.allConnectionFails) return 'Upload failed — your connection was unstable';
        return 'No files were uploaded';
      }
      case 'cancelled-all':
        return 'No files were uploaded';
      default:
        return null;
    }
  })();

  // Time-line copy. Active states come pre-resolved from the VM (wrapper
  // had to read uploadManager). Terminal states fall back to formatTime
  // here since timestamp lives on the VM.
  const timeLine: React.ReactNode = (() => {
    if (vm.timeLineText !== null) return vm.timeLineText;
    if (isActive) return null; // wrap-up explicitly returns null
    return formatTime(vm.timestamp);
  })();

  return (
    <div
      ref={cardRef}
      className={`${css.card} ${vm.thumbnailUrl ? css.hasImage : ''} ${
        state === 'done-partial-failure' || state === 'done-total-failure' ? css.hasIssue : ''
      } ${state === 'cancelled-all' ? css.hasCancelled : ''}`}
    >
      <InlineConfirm
        open={pendingCancel}
        outsideRef={cardRef}
        onConfirm={() => {
          setPendingCancel(false);
          onCancelBatch?.();
        }}
        onCancel={() => setPendingCancel(false)}
        confirmLabel="CONFIRM UPLOAD CANCEL"
        confirmSubLabel={vm.mediaCount > 1 ? 'Some files might have already finished.' : undefined}
        cancelLabel="CONTINUE UPLOAD"
      />
      {state === 'cancelled-all' ? (
        <button
          className={css.dismissBtn}
          onClick={onDismiss}
          aria-label="Dismiss cancelled upload"
        >
          <CloseRounded />
        </button>
      ) : (
        <button
          className={css.kebab}
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          aria-label="More actions"
        >
          <MoreVertRounded />
        </button>
      )}
      <BatchCardMenu
        anchor={menuAnchor}
        onClose={() => setMenuAnchor(null)}
        libraryUrl={vm.libraryUrl}
        canCancel={canCancel}
        canDelete={isProcessing}
        isActive={isActive}
        onRemove={onDismiss}
        retryableCount={vm.retryableCount}
        onViewFailedFiles={vm.retryableCount > 0 && onOpenResults ? onOpenResults : undefined}
        onRetryFailed={vm.retryableCount > 0 && onRetryFailed ? onRetryFailed : undefined}
        onDeleteBatch={isProcessing && onDeleteBatch ? onDeleteBatch : undefined}
        onCancelBatch={canCancel && onCancelBatch ? () => setPendingCancel(true) : undefined}
        mediaCount={vm.mediaCount}
      />
      <div className={css.cardRow}>
        <div className={css.thumbStack}>
          {vm.mediaCount > 1 && (
            <div className={css.thumbStackBack} aria-hidden="true">
              {vm.secondaryThumbnailUrl ? (
                <img src={vm.secondaryThumbnailUrl} alt="" className={css.thumbImage} />
              ) : (
                <PlaceholderGradient
                  id={generateIdFromString(vm.secondaryGradientSeed ?? `${vm.id}-2`)}
                  blur={30}
                />
              )}
              {vm.secondaryCdnPreview && (
                <AuthenticatedImage
                  src={vm.secondaryCdnPreview.url}
                  token={vm.secondaryCdnPreview.token}
                  className={css.thumbImageOverlay}
                />
              )}
              {/* Match the primary thumb's active-state dim, minus the spinner — the
                  back card is decorative, but should still read as "this batch is in
                  progress" alongside the foreground thumb. */}
              {isActive && <div className={css.dimOverlay} />}
            </div>
          )}
          <div className={css.thumb}>
            {vm.thumbnailUrl ? (
              <img src={vm.thumbnailUrl} alt="Media thumbnail" className={css.thumbImage} />
            ) : (
              <>
                <PlaceholderGradient
                  id={generateIdFromString(vm.gradientSeed ?? vm.id)}
                  blur={30}
                />
                <div className={css.thumbIcon}>
                  {vm.isAudio ? <AudioSelectedIcon /> : <VideoSelectedIcon />}
                </div>
              </>
            )}
            {vm.primaryCdnPreview && (
              <AuthenticatedImage
                src={vm.primaryCdnPreview.url}
                token={vm.primaryCdnPreview.token}
                className={css.thumbImageOverlay}
              />
            )}
            {isActive && (
              <div className={css.spinOverlay}>
                <SpinnerArc />
              </div>
            )}
          </div>
        </div>
        <div className={css.body}>
          <div className={`${css.title} ${titleStrikethrough ? css.titleCancelled : ''}`}>
            <span className={css.filename}>{filename}</span>
            {othersCount > 0 && (
              <Tooltip title={othersTooltip ?? ''} placement="bottom" arrow>
                <span className={css.othersCount}>
                  + {othersCount} {othersCount === 1 ? 'other' : 'others'}
                </span>
              </Tooltip>
            )}
          </div>
          <div className={css.meta}>
            <Link
              to={vm.libraryUrl}
              className={`${css.libraryLink} ${titleStrikethrough ? css.libraryLinkCancelled : ''}`}
            >
              {vm.libraryName}
            </Link>
            {statusText && (
              <>
                <span className={css.sep} />
                <span className={`${css.status} ${statusClass}`}>
                  {statusIcon}
                  <span>{statusText}</span>
                </span>
              </>
            )}
          </div>
          {subText && <div className={css.sub}>{subText}</div>}
          {isActive &&
            (() => {
              // Split the filled bar into a failed (red) slice on the LEFT and the
              // actual-progress slice on its RIGHT. Byte-weighted; upload phase only
              // (the failed slice doesn't apply to the processing bar). Where the
              // two meet, square the adjacent corners so they read as one bar.
              const failedPercent = isProcessing
                ? 0
                : Math.min(Math.round(vm.failedProgress * 100), percent);
              const succeededPercent = percent - failedPercent;
              const hasFailed = failedPercent > 0;
              return (
                <div className={css.progress}>
                  <div className={css.pbar}>
                    {hasFailed && (
                      <div
                        // Square the right edge only when progress follows it.
                        className={`${css.pbarFillFailed} ${succeededPercent > 0 ? css.pbarAbutRight : ''}`}
                        style={{ left: 0, width: `${failedPercent}%` }}
                      />
                    )}
                    {succeededPercent > 0 && (
                      <div
                        // Square the left edge only when a failed slice precedes it.
                        className={`${css.pbarFill} ${isProcessing ? css.pbarFillProcessing : ''} ${
                          hasFailed ? css.pbarAbutLeft : ''
                        }`}
                        style={{ left: `${failedPercent}%`, width: `${succeededPercent}%` }}
                      />
                    )}
                  </div>
                  <span className={css.pbarPct}>{percent}%</span>
                </div>
              );
            })()}
          <div className={css.time}>{timeLine}</div>
        </div>
      </div>
      {vm.retryableCount > 0 && vm.hasBillingFails && vm.billedToAnotherAccount && (
        <OwnerBilledActionRow
          retryableCount={vm.retryableCount}
          failedCount={vm.failedCount}
          ownerName={vm.ownerName}
          onRetry={onRetryFailed}
          onOpenResults={onOpenResults}
        />
      )}
      {vm.retryableCount > 0 && vm.hasBillingFails && !vm.billedToAnotherAccount && (
        <BillingActionRow
          showStorageErrors={vm.showStorageErrors}
          showCreditErrors={vm.showCreditErrors}
          retryableCount={vm.retryableCount}
          failedCount={vm.failedCount}
          onRetry={onRetryFailed}
          onOpenResults={onOpenResults}
        />
      )}
      {vm.retryableCount > 0 && !vm.hasBillingFails && (
        <MediaListLink
          variant="error"
          action="View & retry"
          subtext={failedFilesSubtext(vm.retryableCount, vm.failedCount)}
          onClick={() => onOpenResults?.()}
          disabled={!onOpenResults}
        />
      )}
      {vm.skippedDuplicates.length > 0 && <SkippedDuplicatesLink rows={vm.skippedDuplicates} />}
    </div>
  );
}

/** Inline component that owns the FailedMediaListModal state for the
 * "View N duplicates skipped" affordance. Lifted out of BatchCardImpl so
 * the modal open/close state doesn't trigger BatchCard's memo path. */
function SkippedDuplicatesLink({ rows }: { rows: SkippedDuplicateRow[] }) {
  const [isOpen, setIsOpen] = useState(false);
  const count = rows.length;
  // All rows of a skipped-duplicates set belong to the same target library
  // (the one the user was uploading into); fall back to '' only if the
  // upstream forgot to pass one.
  const trackingLibraryId = rows.find((r) => r.libraryId)?.libraryId ?? '';
  const handleOpen = () => {
    trackingStore.uploadDuplicatesSkippedViewed({
      libraryId: trackingLibraryId,
      duplicatesCount: count,
    });
    setIsOpen(true);
  };
  // Same UX as the rich-event duplicate card: clicking "View in library"
  // closes the modal and collapses the panel so the user lands cleanly on
  // the library listing with the matched media flashing.
  const handleViewInLibraryNavigate = useCallback((libraryId: string, mediaId: string) => {
    trackingStore.viewInLibraryClicked({
      source: 'batch-card-duplicates',
      libraryId,
      mediaId,
    });
    setIsOpen(false);
    notificationStore.setPopoverOpen(false);
  }, []);
  return (
    <>
      <MediaListLink
        variant="info"
        action="View"
        subtext={`${count} duplicate${count === 1 ? '' : 's'} skipped`}
        onClick={handleOpen}
      />
      <FailedMediaListModal
        open={isOpen}
        onClose={() => setIsOpen(false)}
        title="Duplicates skipped"
        metaText={`${count} ${count === 1 ? 'file' : 'files'}`}
        groups={[
          {
            key: 'duplicates',
            rows: rows.map((r) => ({
              key: r.key,
              filename: r.filename,
              reason: 'Already in this library',
              isAudio: false,
              thumbnailUrl: null,
              viewInLibrary:
                r.libraryId && r.mediaId
                  ? {
                      libraryId: r.libraryId,
                      mediaId: r.mediaId,
                      onNavigate: () => handleViewInLibraryNavigate(r.libraryId!, r.mediaId!),
                    }
                  : undefined,
            })),
          },
        ]}
        variant="warning"
      />
    </>
  );
}

export const BatchCard = memo(BatchCardImpl);
